import { useRef } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useFeedback } from "../../../components/DCKeeper";
import { useMarkMessagesRead, readFailureReason } from "../../../query/useMessages";
import { hasRoute } from "../../../routes/allRoutes";

/**
 * 訊息的使用者動作(notify-api 票 05／06):訊息查詢頁與小鈴鐺**共用同一份**,兩邊行為才不會分岔
 * (票 06 review 抓到:鈴鐺標已讀失敗照樣導頁、頁面則不導;連點防重只有頁面有)。
 *
 * - `markRead(m)` → "ok"(已讀或本來就讀過)/"busy"(這一則正在送,連點)/"failed"(已提示)。
 *   「正在送」按訊息 id 記,不用 mutation 共用的 isPending —— 否則 A 還在送時點 B 會被跳過。
 * - `open(m, { fallbackUrl })`:先標已讀,成功才去前往連結(沒有就 fallbackUrl);失敗留在原地、可再試。
 * - `markAllRead()`:成功提示＋三組查詢重抓完才結束(isPending 涵蓋到重抓完)。
 */
export const isInternalUrl = (url) => typeof url === "string" && url.startsWith("/");

const warnedDeadLinks = new Set();
/**
 * 站內前往連結對不到部署站的路由(例:審核中心藏著時的「前往審核」→ /dck/approvals)就拿掉 ——
 * 點下去落到「找不到這個頁面」比沒有連結更糟。值是後端資料,拿掉時 warn 一次(ADR-0004 不安靜降級)。
 */
export function withReachableAction(view) {
  const url = view && view.action && view.action.url;
  if (!url || !isInternalUrl(url) || hasRoute(url)) return view;
  if (!warnedDeadLinks.has(url)) {
    warnedDeadLinks.add(url);
    console.warn(`[notify] 訊息的前往連結「${url}」在這個站沒有對應頁面(模組未開放),不顯示前往按鈕。`);
  }
  return { ...view, action: null };
}

export function useMessageActions() {
  const { t } = useTranslation();
  const history = useHistory();
  const fb = useFeedback();
  const { markOne, markAll } = useMarkMessagesRead();
  const inFlight = useRef(new Set());

  const fail = (err) => fb.error(t("notify.markReadFailed", { reason: readFailureReason(err) }));

  const markRead = async (m) => {
    if (!m.unread) return "ok";
    if (inFlight.current.has(m.id)) return "busy";
    inFlight.current.add(m.id);
    try {
      await markOne.mutateAsync(m.id);
      return "ok";
    } catch (err) {
      fail(err);
      return "failed";
    } finally {
      inFlight.current.delete(m.id);
    }
  };

  const open = async (m, { fallbackUrl } = {}) => {
    if ((await markRead(m)) !== "ok") return false;
    const url = m.action?.url || fallbackUrl;
    if (!url) return true;
    if (isInternalUrl(url)) history.push(url);
    else window.location.assign(url);
    return true;
  };

  const markAllRead = async () => {
    try {
      await markAll.mutateAsync();
      fb.success(t("notify.allMarkedRead"));
      return true;
    } catch (err) {
      fail(err);
      return false;
    }
  };

  return { markRead, open, markAllRead, markAllPending: markAll.isPending };
}
